import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Building2, Users, CreditCard, Palette, Shield, Mail, Hash, Activity, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isBusinessAdmin } from '../../utils/permissions';
import organizationService from '../../services/organizationService';
import MemberManagementSection from './components/MemberManagementSection';
import InviteManagementSection from './components/InviteManagementSection';
import BillingIntegrationSection from './components/BillingIntegrationSection';
import BrandingControlsSection from './components/BrandingControlsSection';
import RoleAccessControlSection from './components/RoleAccessControlSection';
import Icon from '../../components/AppIcon';
import AllOrganizationsTable from './components/AllOrganizationsTable';
import AddMemberModal from './components/AddMemberModal';



export default function OrganizationAdminPanel() {
  const { profile, userRoles } = useAuth();
  const [activeTab, setActiveTab] = useState('all-organizations');
  const [stats, setStats] = useState({
    totalMembers: 0,
    totalOrganizations: 0
  });
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedOrgForMember, setSelectedOrgForMember] = useState(null);
  const [organizationInfo, setOrganizationInfo] = useState(null);

  // Check if user has admin access - use userRoles from AuthContext, not profile.userRoles
  const hasAdminAccess = isBusinessAdmin(userRoles, profile?.tenant_id);

  useEffect(() => {
    if (!hasAdminAccess) {
      // Redirect or show access denied
      return;
    }
    loadDashboardStats();
    loadOrganizationInfo();
  }, [hasAdminAccess]);

  const loadDashboardStats = async () => {
    try {
      // Initialize with default values including organizationHealth
      setStats({
        totalMembers: 0,
        totalOrganizations: 0,
        pendingInvites: 0,
        activeSubscription: 'Loading...',
        organizationHealth: 98 // Default health percentage
      });
      // Stats will be loaded by individual sections
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const loadOrganizationInfo = async () => {
    try {
      const memberships = await organizationService?.getUserMemberships();
      const currentOrg = memberships?.find(m => m?.tenantId === profile?.tenant_id);
      if (currentOrg) {
        setOrganizationInfo({
          tenantId: currentOrg?.tenantId,
          name: currentOrg?.tenant?.name,
          subdomain: currentOrg?.tenant?.subdomain,
          logoUrl: currentOrg?.tenant?.logoUrl
        });
      }
    } catch (error) {
      console.error('Error loading organization info:', error);
    }
  };

  const updateStats = (newStats) => {
    setStats(prev => ({ ...prev, ...newStats }));
  };

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Organization Admin Panel.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'organizations', label: 'All Organizations', icon: Building2 },
    { id: 'members', label: 'Member Management', icon: Users },
    { id: 'invites', label: 'Invite Codes', icon: Mail },
    { id: 'access-control', label: 'Role Access Control', icon: Shield },
    { id: 'billing', label: 'Billing & Subscription', icon: CreditCard },
    { id: 'branding', label: 'Branding Controls', icon: Palette }
  ];

  const handleSwitchOrganization = async (tenantId) => {
    try {
      await organizationService?.switchOrganizationContext(tenantId);
      // Reload page to refresh all data with new organization context
      window.location?.reload();
    } catch (error) {
      console.error('Error switching organization:', error);
      alert('Failed to switch organization. Please try again.');
    }
  };

  const handleAddMemberToOrg = (org) => {
    setSelectedOrgForMember(org);
    setShowAddMemberModal(true);
  };

  const handleMemberAdded = () => {
    // Refresh data if needed
    if (activeTab === 'members') {
      // Member management section will auto-refresh
    }
  };

  return (
    <>
      <Helmet>
        <title>Organization Admin Panel - CRM</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Organization Admin Panel</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage your organization's members, invitations, billing, and branding
                </p>
                {/* Organization Info Display */}
                {organizationInfo && (
                  <div className="mt-3 flex items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">{organizationInfo?.name}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg">
                      <Hash className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-mono text-purple-900">{organizationInfo?.tenantId?.slice(0, 8)}...</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                  <Activity className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs text-gray-600">System Health</p>
                    <p className="text-sm font-semibold text-green-600">{stats?.organizationHealth}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Members</p>
                    <p className="text-2xl font-bold text-blue-600">{stats?.totalMembers}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Invites</p>
                    <p className="text-2xl font-bold text-purple-600">{stats?.pendingInvites}</p>
                  </div>
                  <Mail className="w-8 h-8 text-purple-600" />
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Subscription</p>
                    <p className="text-lg font-bold text-green-600">{stats?.activeSubscription}</p>
                  </div>
                  <CreditCard className="w-8 h-8 text-green-600" />
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Monthly Growth</p>
                    <p className="text-2xl font-bold text-orange-600">+12%</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    {tab?.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'organizations' && <AllOrganizationsTable onSwitchOrganization={handleSwitchOrganization} />}
          {activeTab === 'members' && <MemberManagementSection onStatsUpdate={updateStats} />}
          {activeTab === 'invites' && <InviteManagementSection onStatsUpdate={updateStats} />}
          {activeTab === 'access-control' && <RoleAccessControlSection />}
          {activeTab === 'billing' && <BillingIntegrationSection onStatsUpdate={updateStats} />}
          {activeTab === 'branding' && <BrandingControlsSection />}
        </div>

        {/* Add Member Modal (Global) */}
        {selectedOrgForMember && (
          <AddMemberModal
            isOpen={showAddMemberModal}
            onClose={() => {
              setShowAddMemberModal(false);
              setSelectedOrgForMember(null);
            }}
            tenantId={selectedOrgForMember?.tenantId}
            tenantName={selectedOrgForMember?.name}
            onMemberAdded={handleMemberAdded}
          />
        )}
      </div>
    </>
  );
}